/**
 * 피코 이벤트 반응 규칙 표(SR-ACH-03 — 이벤트 ≥8종 · 규칙 테이블은 카탈로그).
 * 카탈로그 `GET /api/catalog?type=pico_reaction` 의 항목 {key, i18n:{ko,en}, payload:{event, emoji?, animation?}} 가
 * 이벤트별 반응(문구 · 표정 · 애니메이션)을 정한다. 카탈로그에 없는 이벤트(또는 못 받았을 때)는 이 파일의 로컬 표로 대신한다.
 * 문구는 카탈로그 i18n(운영자 화이트리스트) 또는 셸 문구 카탈로그 키(pico.event.<event>.<se|su>)에서만 온다(자유 텍스트 0 · DR-02).
 * SE 는 읽어 주기(ReadAloud) — 말풍선·토스트가 data-read-aloud 를 단다. Spine 애니메이션 자리는 animation 키(지금은 CSS 반응).
 */
import type { Locale, Translate } from "../../../i18n";
import type { PicoAnimation, PicoReaction, PicoTable } from "../types";

export const PICO_EVENTS = [
  "welcome",
  "upload-done",
  "first-upload",
  "course-finish",
  "course-fail",
  "retry",
  "gallery-view",
  "sticker-given",
  "break",
  "replay-saved",
  // P2-S6 배지(SR-ACH-02 — 룸 ev badge)
  "badge",
] as const;
export type PicoEvent = (typeof PICO_EVENTS)[number];

/** 로컬 대체 표 — 카탈로그에 없는 이벤트 */
const LOCAL: Record<PicoEvent, { avatar: string; animation: PicoAnimation }> = {
  welcome: { avatar: "🤖", animation: "wave" },
  "upload-done": { avatar: "🥳", animation: "cheer" },
  "first-upload": { avatar: "🎉", animation: "cheer" },
  "course-finish": { avatar: "🥳", animation: "cheer" },
  "course-fail": { avatar: "🤖", animation: "think" },
  retry: { avatar: "💪", animation: "wave" },
  "gallery-view": { avatar: "👀", animation: "wave" },
  "sticker-given": { avatar: "😊", animation: "cheer" },
  break: { avatar: "🍵", animation: "rest" },
  "replay-saved": { avatar: "💾", animation: "cheer" },
  badge: { avatar: "🏅", animation: "cheer" },
};

/** 카탈로그 애니메이션 키 → 셸 CSS 반응(Spine 전까지). 모르는 키는 이벤트 기본 */
const ANIMS: Record<string, PicoAnimation> = {
  cheer: "cheer",
  bounce: "cheer",
  confetti: "cheer",
  sparkle: "cheer",
  clap: "cheer",
  wave: "wave",
  nod: "wave",
  think: "think",
  rest: "rest",
  stretch: "rest",
  idle: "idle",
};
/** 카탈로그 초기 시드(S4)의 이벤트 이름 → S5 이벤트 */
const ALIASES: Record<string, PicoEvent> = {
  complete: "course-finish",
  finish: "course-finish",
  fail: "course-fail",
  upload: "upload-done",
  "upload-complete": "upload-done",
  sticker: "sticker-given",
  "break-ack": "break",
};

const isEvent = (s: string): s is PicoEvent => (PICO_EVENTS as readonly string[]).includes(s);

export function localReaction(event: PicoEvent): PicoReaction {
  const l = LOCAL[event];
  return {
    event,
    avatar: l.avatar,
    animation: l.animation,
    textKey: `pico.event.${event}`,
    source: `local:${event}`,
  };
}

/** 카탈로그 응답({items:[…]} 또는 배열)을 너그럽게 읽어 이벤트 → 반응 표로. 모르는 이벤트는 버린다 */
export function readPicoCatalog(v: unknown): Map<PicoEvent, PicoReaction> {
  const list: unknown[] = Array.isArray(v)
    ? v
    : Array.isArray((v as { items?: unknown })?.items)
      ? (v as { items: unknown[] }).items
      : [];
  const out = new Map<PicoEvent, PicoReaction>();
  for (const raw of list) {
    const o = (raw ?? {}) as Record<string, unknown>;
    if (o.type != null && o.type !== "pico_reaction") continue;
    if (o.enabled === false) continue;
    const p = ((o.payload ?? o.data ?? {}) as Record<string, unknown>) ?? {};
    const rawEvent = typeof p.event === "string" ? p.event : null;
    if (!rawEvent) continue;
    const event = isEvent(rawEvent) ? rawEvent : ALIASES[rawEvent];
    if (!event || out.has(event)) continue;
    const base = localReaction(event);
    const i18n = (o.i18n ?? null) as { ko?: unknown; en?: unknown } | null;
    const text =
      i18n && (typeof i18n.ko === "string" || typeof i18n.en === "string")
        ? {
            ko: typeof i18n.ko === "string" ? i18n.ko : undefined,
            en: typeof i18n.en === "string" ? i18n.en : undefined,
          }
        : null;
    const anim = (p.animation ?? p.pose) as string | undefined;
    out.set(event, {
      ...base,
      avatar: typeof p.emoji === "string" && p.emoji ? p.emoji : base.avatar,
      animation: (anim && ANIMS[anim]) || base.animation,
      text,
      source: typeof o.key === "string" ? o.key : base.source,
    });
  }
  return out;
}

/** 이벤트 반응 — 카탈로그 표에 있으면 그것, 없으면 로컬 표 */
export function picoReaction(event: PicoEvent, table?: PicoTable | null): PicoReaction {
  return table?.get(event) ?? localReaction(event);
}

/**
 * 반응 문구 — 카탈로그 문구(로캘 → ko) 우선, 없으면 셸 키(밀도별 `.se` · `.su`, 없으면 공통).
 * 카탈로그 문구도 DR 검사(점수·비교 금칙)는 운영 콘솔 저장 때 받는다.
 */
export function reactionText(
  t: Translate,
  locale: Locale,
  density: "se" | "su",
  r: PicoReaction,
  has: (key: string) => boolean,
): string {
  const cat = r.text?.[locale] ?? r.text?.ko;
  if (cat && cat.trim()) return cat;
  const k = `${r.textKey}.${density}`;
  return has(k) ? t(k) : t(r.textKey);
}
